import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import {
  ShieldCheck,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  FileText,
  Users,
  Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TOGGLES = [
  {
    key: "aggregate_data_safety_controls",
    label: "Aggregate Data Safety Controls",
    description: "Master switch — enforces aggregate-only behavior across MySchoolRx.",
    impact: "high",
  },
  {
    key: "evidence_upload_attestation_required",
    label: "Evidence Upload Attestation Required",
    description: "Users must check the no-student-PII attestation checkbox before any evidence upload.",
    impact: "high",
  },
  {
    key: "ai_pii_guardrails",
    label: "AI PII Guardrails",
    description: "Apply small-cell suppression and PII filtering before AI prompt construction.",
    impact: "high",
  },
  {
    key: "block_ai_when_risk_flag",
    label: "Block AI when Risk Flag Exists",
    description: "Refuses AI generation if any input evidence is auto-flagged or unattested.",
    impact: "high",
  },
  {
    key: "student_record_upload_warning",
    label: "Student Record Upload Warning",
    description: "Shows the explicit warning copy in evidence upload modals.",
    impact: "medium",
  },
  {
    key: "require_terms_reacceptance_on_data_safety_update",
    label: "Require Terms Re-Acceptance After Data Safety Update",
    description: "Force users to re-accept Terms whenever a new data-safety Terms version is published.",
    impact: "high",
  },
  {
    key: "allow_super_admin_evidence_removal",
    label: "Allow Super Admin Evidence Removal",
    description: "Super Admin can remove flagged or replaced evidence.",
    impact: "medium",
  },
  {
    key: "allow_tenant_admin_evidence_removal",
    label: "Allow Tenant Admin Evidence Removal",
    description: "Tenant Admin can remove evidence on their tenant.",
    impact: "medium",
  },
];

const SMALL_CELL_MODES = [
  { value: "suppress", label: "Suppress", help: "Hide values below threshold (default)" },
  { value: "aggregate", label: "Aggregate", help: "Replace value with “<N”" },
  { value: "warn_only", label: "Warn Only", help: "Display value with a warning badge" },
];

const buildHeaders = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id": session?.user?.id || "admin",
  "x-user-role": session?.user?.role || "admin",
  "Content-Type": "application/json",
});

function ToggleRow({ toggle, value, onChange, disabled }) {
  return (
    <div
      className="flex items-start justify-between gap-4 py-4"
      data-testid={`ds-toggle-${toggle.key}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[13.5px] font-bold text-[#0f172a]">{toggle.label}</div>
          {toggle.impact === "high" && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-[0.06em] uppercase"
              style={{ background: "#fee2e2", color: "#b91c1c" }}
            >
              High impact
            </span>
          )}
        </div>
        <div className="mt-0.5 text-[12px] text-[#64748b] leading-relaxed">
          {toggle.description}
        </div>
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!value)}
        disabled={disabled}
        className="shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50"
        style={{ background: value ? "#16a34a" : "#cbd5e1" }}
        aria-pressed={value}
        data-testid={`ds-toggle-switch-${toggle.key}`}
      >
        <span
          className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
          style={{ transform: value ? "translateX(22px)" : "translateX(2px)" }}
        />
      </button>
    </div>
  );
}

export default function DataSafetySettings({ session }) {
  const [settings, setSettings] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = useMemo(() => buildHeaders(session), [session]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, o] = await Promise.all([
        axios.get(`${API}/api/admin/data-safety/settings`, { headers }),
        axios.get(`${API}/api/admin/data-safety/overview`, { headers }),
      ]);
      setSettings(s.data);
      setOverview(o.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Data Safety settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const patchSetting = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/api/admin/data-safety/settings`, patch, { headers });
      setSettings(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="data-safety-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#4b5563]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="data-safety-settings">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-[26px] sm:text-[28px] font-bold tracking-[-0.02em] text-[#0f172a] flex items-center gap-2.5" data-testid="data-safety-title">
            <ShieldCheck className="h-6 w-6 text-[#16a34a]" />
            Data Safety
          </h1>
          <p className="mt-1 text-[12.5px] sm:text-[13px] text-[#64748b] max-w-2xl">
            Aggregate Data Safety Controls for MySchoolRx. Defaults are safe — only Super Admin can change them.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#e5e7eb] bg-white text-[12px] font-semibold text-[#475569] hover:bg-[#f8fafc] self-start sm:self-auto"
          data-testid="data-safety-refresh-btn"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Overview */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="data-safety-overview">
          <OverviewTile
            icon={<FileText className="h-4 w-4" />}
            label="Active Terms Version"
            value={overview.active_terms?.version || "—"}
            sub={overview.active_terms?.title?.replace(/^MySchoolRx /, "") || "—"}
            accent="#3B4FE4"
            testid="overview-active-terms"
          />
          <OverviewTile
            icon={<Users className="h-4 w-4" />}
            label="Tenants Accepted"
            value={`${overview.terms_acceptances_for_active || 0}`}
            sub={`of ${overview.tenants_total || 0} tenants`}
            accent="#16a34a"
            testid="overview-tenants-accepted"
          />
          <OverviewTile
            icon={<CheckCircle2 className="h-4 w-4" />}
            label="Evidence Attested"
            value={`${overview.evidence_attested || 0}`}
            sub={`of ${overview.evidence_total || 0} files`}
            accent="#0891b2"
            testid="overview-evidence-attested"
          />
          <OverviewTile
            icon={<AlertTriangle className="h-4 w-4" />}
            label="Evidence Flagged / Removed"
            value={`${(overview.evidence_flagged || 0) + (overview.evidence_removed || 0)}`}
            sub={`${overview.evidence_flagged || 0} flagged · ${overview.evidence_removed || 0} removed`}
            accent="#C79A3B"
            testid="overview-evidence-flagged"
          />
        </div>
      )}

      {/* Min Cell Size */}
      <section
        className="rounded-2xl border bg-white p-5 sm:p-6"
        style={{ borderColor: "#e5e7eb" }}
        data-testid="ds-section-min-cell"
      >
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-[15px] font-bold text-[#0f172a]">Small-Cell Suppression</h2>
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-[0.06em] uppercase"
            style={{ background: "#fee2e2", color: "#b91c1c" }}
          >
            High impact
          </span>
        </div>
        <p className="text-[12.5px] text-[#64748b] mb-4">
          Subgroup counts below the threshold are suppressed (or aggregated, depending on handling mode) in dashboards, reports, AI outputs, and exports.
          Allowed range: 5 – 25. Default: 10.
        </p>
        <div className="flex flex-col sm:flex-row gap-5">
          <label className="flex-1 min-w-0" data-testid="ds-min-cell-size-row">
            <div className="text-[11.5px] font-bold tracking-[0.06em] uppercase text-[#475569] mb-1">
              Minimum Cell Size
            </div>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={5}
                max={25}
                value={settings.min_cell_size}
                onChange={(e) => setSettings({ ...settings, min_cell_size: parseInt(e.target.value, 10) })}
                onMouseUp={(e) => patchSetting({ min_cell_size: parseInt(e.target.value, 10) })}
                onTouchEnd={(e) => patchSetting({ min_cell_size: parseInt(e.target.value, 10) })}
                className="flex-1"
                data-testid="ds-min-cell-size-slider"
              />
              <div
                className="min-w-[44px] text-center rounded-md border border-[#e5e7eb] py-1 px-2 text-[14px] font-bold text-[#0f172a]"
                data-testid="ds-min-cell-size-value"
              >
                {settings.min_cell_size}
              </div>
            </div>
          </label>
          <label className="flex-1 min-w-0" data-testid="ds-small-cell-handling-row">
            <div className="text-[11.5px] font-bold tracking-[0.06em] uppercase text-[#475569] mb-1">
              Handling
            </div>
            <select
              value={settings.small_cell_handling}
              onChange={(e) => patchSetting({ small_cell_handling: e.target.value })}
              className="w-full h-10 rounded-md border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#0f172a]"
              data-testid="ds-small-cell-handling-select"
            >
              {SMALL_CELL_MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label} — {m.help}</option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {/* Toggles */}
      <section
        className="rounded-2xl border bg-white p-5 sm:p-6"
        style={{ borderColor: "#e5e7eb" }}
        data-testid="ds-section-toggles"
      >
        <h2 className="text-[15px] font-bold text-[#0f172a] mb-1">Policy Toggles</h2>
        <p className="text-[12.5px] text-[#64748b] mb-2">
          These switches govern the data-safety enforcement layer. Safe-by-default — leave them on unless you have a documented exception.
        </p>
        <div className="divide-y" style={{ borderColor: "#e5e7eb" }}>
          {TOGGLES.map((t) => (
            <ToggleRow
              key={t.key}
              toggle={t}
              value={!!settings[t.key]}
              onChange={(v) => patchSetting({ [t.key]: v })}
              disabled={saving}
            />
          ))}
        </div>
      </section>

      {/* Footer disclaimer */}
      <div
        className="rounded-xl border p-4 text-[12px] text-[#475569] flex items-start gap-2.5"
        style={{ borderColor: "#fde68a", background: "#fffbeb" }}
        data-testid="data-safety-disclaimer"
      >
        <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-[#C79A3B]" />
        <span>
          This update supports compliance and data safety but does not provide legal advice and does not guarantee compliance.
          The school remains responsible for reviewing submissions, uploads, and legal/compliance obligations.
        </span>
      </div>
    </div>
  );
}

function OverviewTile({ icon, label, value, sub, accent, testid }) {
  return (
    <div
      className="rounded-xl border bg-white p-4"
      style={{ borderColor: "#e5e7eb" }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center justify-center h-7 w-7 rounded-md"
          style={{ background: `${accent}15`, color: accent }}
        >
          {icon}
        </span>
        <div className="text-[11.5px] font-bold tracking-[0.12em] uppercase text-[#4b5563]">
          {label}
        </div>
      </div>
      <div className="mt-2 text-[22px] font-bold tracking-[-0.02em] text-[#0f172a] leading-none">
        {value}
      </div>
      {sub && <div className="mt-1.5 text-[11.5px] text-[#64748b]">{sub}</div>}
    </div>
  );
}
